import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../../common/api/ApiRequestAdapter";
import { apiService } from "../../../common/api/apiService";
import { HttpMethod } from "../../../common/api/http";
import {
  EntityIdObject,
  EntityObject,
  OptimisticLockVersion,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject
} from "../../../common/types";
import {
  AssignCommissionsSettingsLevel,
  AssignCommissionsSettingsRank,
  CommissionsSettings,
  CommissionsSettingsAgentProfile,
  CommissionsSettingsAttachment,
  CommissionsSettingsRule,
  CreateCommissionsSettingsRule,
  UpdateCommissionsSettingsRule
} from "./types";

const GET_COMMISSIONS_SETTINGS = ApiRequestAdapter.create("/agents/{0}/commissions-settings", HttpMethod.GET);
const GET_PROFILE_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/profile",
  HttpMethod.GET
);
const ASSIGN_COMMISSIONS_SETTINGS_LEVEL = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/assign-level",
  HttpMethod.PUT
);
const UNASSIGN_COMMISSIONS_SETTINGS_LEVEL = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/unassign-level",
  HttpMethod.PUT
);
const ASSIGN_COMMISSIONS_SETTINGS_RANK = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/assign-rank",
  HttpMethod.PUT
);
const UNASSIGN_COMMISSIONS_SETTINGS_RANK = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/unassign-rank",
  HttpMethod.PUT
);
const TOGGLE_PAYMAN_INCLUSION_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/toggle-payment-inclusion",
  HttpMethod.PUT
);
const TOGGLE_USAGE_AS_HEAD_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/toggle-usage-as-head",
  HttpMethod.PUT
);
const TOGGLE_POINTS_UNIFICATION_COMMISSIONS_SETTINGS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/toggle-points-unification",
  HttpMethod.PUT
);
const CREATE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules",
  HttpMethod.POST
);
const UPDATE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules/{1}",
  HttpMethod.PUT
);
const DELETE_COMMISSIONS_SETTINGS_RULE = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/rules/{1}",
  HttpMethod.DELETE
);
const UPLOAD_COMMISSIONS_SETTINGS_ATTACHMENTS = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments",
  HttpMethod.POST
);
const DOWNLOAD_COMMISSIONS_SETTINGS_ATTACHMENT = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments/{1}",
  HttpMethod.GET
);
const DELETE_COMMISSIONS_SETTINGS_ATTACHMENT = ApiRequestAdapter.create(
  "/agents/{0}/commissions-settings/attachments/{1}",
  HttpMethod.DELETE
);

export const commissionsSettingsApi = {
  getCommissionsSettings: (request: EntityIdObject): AxiosPromise<CommissionsSettings> => {
    return apiService.get(GET_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id));
  },
  getProfileCommissionsSettings: (request: EntityIdObject): AxiosPromise<CommissionsSettingsAgentProfile> => {
    return apiService.get(GET_PROFILE_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id));
  },
  assignCommissionsSettingsLevel: (
    request: EntityObject<AssignCommissionsSettingsLevel>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(ASSIGN_COMMISSIONS_SETTINGS_LEVEL.getParametrizedUrl(request.id), request.object);
  },
  unassignCommissionsSettingsLevel: (
    request: EntityObject<OptimisticLockVersion>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(UNASSIGN_COMMISSIONS_SETTINGS_LEVEL.getParametrizedUrl(request.id), request.object);
  },
  assignCommissionsSettingsRank: (
    request: EntityObject<AssignCommissionsSettingsRank>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(ASSIGN_COMMISSIONS_SETTINGS_RANK.getParametrizedUrl(request.id), request.object);
  },
  unassignCommissionsSettingsRank: (
    request: EntityObject<OptimisticLockVersion>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(UNASSIGN_COMMISSIONS_SETTINGS_RANK.getParametrizedUrl(request.id), request.object);
  },
  togglePaymentInclusionCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_PAYMAN_INCLUSION_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  toggleUsageAsHeadCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(TOGGLE_USAGE_AS_HEAD_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id), request.object);
  },
  togglePointsUnificationCommissionsSettings: (
    request: EntityObject<OptimisticLockVersion>
  ): AxiosPromise<CommissionsSettings> => {
    return apiService.put(
      TOGGLE_POINTS_UNIFICATION_COMMISSIONS_SETTINGS.getParametrizedUrl(request.id),
      request.object
    );
  },
  createCommissionsSettingsRule: (
    request: EntityObject<CreateCommissionsSettingsRule>
  ): AxiosPromise<CommissionsSettingsRule> => {
    return apiService.post(CREATE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id), request.object);
  },
  updateCommissionsSettingsRule: (
    request: TwoLevelEntityObject<UpdateCommissionsSettingsRule>
  ): AxiosPromise<CommissionsSettingsRule> => {
    return apiService.put(
      UPDATE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id1, request.id2),
      request.object
    );
  },
  deleteCommissionsSettingsRule: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_SETTINGS_RULE.getParametrizedUrl(request.id1, request.id2));
  },
  uploadCommissionsSettingsAttachments: (
    request: EntityObject<FormData>
  ): AxiosPromise<CommissionsSettingsAttachment[]> => {
    return apiService.post(UPLOAD_COMMISSIONS_SETTINGS_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { "Content-Type": "multipart/form-data" }
    });
  },
  downloadCommissionsSettingsAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_COMMISSIONS_SETTINGS_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob"
    });
  },
  deleteCommissionsSettingsAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_COMMISSIONS_SETTINGS_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  }
};
